import { useEffect, useRef, useState, useCallback } from "react";

// Shared observer map keyed by threshold
const observerMap = new Map<number, IntersectionObserver>();
const callbackMap = new Map<Element, (isIntersecting: boolean) => void>();

function getSharedObserver(threshold: number): IntersectionObserver {
  let observer = observerMap.get(threshold);
  if (!observer) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const cb = callbackMap.get(entry.target);
          if (cb && entry.isIntersecting) {
            cb(true);
            observer!.unobserve(entry.target);
            callbackMap.delete(entry.target);
          }
        });
      },
      { threshold }
    );
    observerMap.set(threshold, observer);
  }
  return observer;
}

export function useInView(options?: IntersectionObserverInit) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const threshold = options?.threshold as number ?? 0.1;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = getSharedObserver(threshold);
    callbackMap.set(el, setInView);
    observer.observe(el);
    return () => {
      observer.unobserve(el);
      callbackMap.delete(el);
    };
  }, [threshold]);

  return { ref, inView };
}
